import React, { useState, useEffect } from 'react';
import { X, Calendar, ShieldCheck, Headphones, ScanLine, ChevronRight, ClipboardList, Construction } from 'lucide-react';
import emailIcon from '../../assets/Icons/email.png';
import WhatsAppIcon from '../../assets/Icons/WhatsAppIcon';
import { useParams, useNavigate } from 'react-router-dom';
import { countInsurancePatientByMail,checkClientByName, validatePatientKyc } from '../../Services/APIs/patient';
import toothlensLogo from "../../assets/illustrations/ToothLens Logo_Blue Black.png";
import PlumAppointment from '../PlumAppointment/plumAppointment';

const TestingIframeLink = () => {
  const navigate = useNavigate();
  const [showPopup, setShowPopup] = useState(false);
  const [showAppointment, setShowAppointment] = useState(false);
  const [showPatientSelection, setShowPatientSelection] = useState(false);
  const [patientsList, setPatientsList] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [patientData, setPatientData] = useState(null); // Store patient data
  const [isValidatingClient, setIsValidatingClient] = useState(true);
  const [isValidCompany, setIsValidCompany] = useState(false);
  const { company } = useParams(); // Get company from URL params or localStorage
  const [insuranceCompany, setInsuranceCompany] = useState('');
  const [showSessionTimeout, setShowSessionTimeout] = useState(false);
  const [kycRequired, setKycRequired] = useState(
    localStorage.getItem('insuranceKycRequired') !== 'false'
  );
  const [kycPendingDetails, setKycPendingDetails] = useState([]);
  const [kycBlockedReason, setKycBlockedReason] = useState('');
  const [showSupportPopup, setShowSupportPopup] = useState(false);

  // Check authentication on mount
  useEffect(() => {
    const loginEmail = localStorage.getItem('insuranceLoginEmail');
    const storedCompany = localStorage.getItem('insuranceCompany');
    if (!loginEmail || !storedCompany) {
      navigate('/insurance/login');
      return;
    }
  }, [navigate]);

  // Auto logout after 2 hours
  useEffect(() => {
    const autoLogoutTimer = setTimeout(() => {
      setShowSessionTimeout(true);
    }, 2 * 60 * 60 * 1000); // 2 hours in milliseconds

    return () => clearTimeout(autoLogoutTimer);
  }, [navigate]);

  // Validate company parameter on mount
  useEffect(() => {
    const validateClient = async () => {
      // Get company from localStorage if not in URL params
      const companyName = company || localStorage.getItem('insuranceCompany');
      setInsuranceCompany(companyName);
      
      if (!companyName) {
        setIsValidatingClient(false);
        setIsValidCompany(false);
        return;
      }

      try {
        const response = await checkClientByName(companyName);
        if (response.status === 200 && response.data?.data) {
          setIsValidCompany(true);
        } else {
          setIsValidCompany(false);
        }
      } catch (error) {
        setIsValidCompany(false);
      } finally {
        setIsValidatingClient(false);
      }
    };

    validateClient();
  }, [company]);



  // Show loading state while validating
  if (isValidatingClient) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-500 via-purple-600 to-indigo-700 flex items-center justify-center p-5">
        <div className="bg-white rounded-2xl p-8 text-center max-w-md">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-700">Validating...</p>
        </div>
      </div>
    );
  }

  // If invalid company, show error message
  if (!isValidCompany) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-500 via-red-600 to-red-700 flex items-center justify-center p-5">
        <div className="bg-white rounded-2xl p-8 text-center max-w-md">
          <h1 className="text-2xl font-bold text-red-600 mb-4">Invalid URL</h1>
          <p className="text-gray-700 mb-4">
            The company "{insuranceCompany}" is not supported. Please use a valid company name.
          </p>
        </div>
      </div>
    );
  }

  const handleExplorePlan = () => {
    window.open('https://toothlens.com/dental360/terms', '_blank', 'noopener,noreferrer');
  };

  const handleClosePopup = () => {
    setShowPopup(false);
  };

  const handleBookAppointment = async () => {
    const storedEmail = localStorage.getItem('insuranceLoginEmail');
    
    if (!storedEmail) {
      setErrorMessage('No email found. Please login again.');
      navigate('/insurance/login');
      return;
    }

    setIsLoading(true);
    setErrorMessage('');

    try {
      const formData = new FormData();
      formData.append('email', storedEmail);
      formData.append('company', localStorage.getItem('insuranceCompany') || insuranceCompany);

      const response = await countInsurancePatientByMail(formData);
      
      if (response.status === 200) {
        const data = response.data?.data || {};
        const patients = data.patients || [];
        const kycPending = data.kyc_pending || [];
        const employeeEmail = data.employee_mail || storedEmail;
        const phoneNumber = data.phone_number || localStorage.getItem('insurancePhoneNumber') || '';

        if (!kycRequired) {
          // ── kyc_required: false ──────────────────────────────────────
          // Patients array comes directly from the wallet; allow booking without KYC
          if (patients.length === 0) {
            setErrorMessage('No members found for this account.');
          } else if (patients.length === 1) {
            setPatientData({
              uid: patients[0].uid,
              name: patients[0].name,
              email: employeeEmail,
              mobile: phoneNumber
            });
            setShowAppointment(true);
          } else {
            // Multiple members — show selection
            const selectable = patients.map((p) => ({
              uid: p.uid,
              name: p.name,
              email: employeeEmail,
              phone_number: phoneNumber,
              relation_to_patient: p.relationship || ''
            }));
            setPatientsList(selectable);
            setShowPatientSelection(true);
          }
        } else {
          // ── kyc_required: true ───────────────────────────────────────
          // Build combined status list
          const getStatus = (review_status) => {
            const map = {
              reviewed: 'Active',
              rejected: 'Rejected',
              retake: 'Retake Requested',
              xray_request: 'X-Ray Requested',
              new: 'In Process',
              retaken: 'In Process',
            };
            return map[review_status] || 'Pending';
          };

          const combined = [
            ...patients.map((p) => ({
              uid: p.uid,
              name: p.name || p.uid,
              relation: p.relationship || '—',
              status: getStatus(p.review_status),
              canBook: p.review_status === 'reviewed',
              patientData: p.review_status === 'reviewed'
                ? { uid: p.uid, name: p.name, email: p.email, mobile: p.phone_number }
                : null
            })),
            ...kycPending.map((p) => ({
              uid: p.uid,
              name: p.name || p.uid,
              relation: p.relationship || '—',
              status: 'Pending',
              canBook: false,
              patientData: null
            }))
          ];

          if (combined.length === 0) {
            setErrorMessage('No members found for this account.');
            return;
          }

          // Always show the KYC status modal — single member goes directly to appointment
          if (combined.length === 1 && combined[0].canBook) {
            setPatientData(combined[0].patientData);
            setShowAppointment(true);
          } else {
            // Show combined KYC status modal with Book buttons for reviewed members
            setKycPendingDetails(combined);
          }
        }
      }
    } catch (error) {
      // API error or patient not found
      setErrorMessage('Something went wrong. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCloseAppointment = () => {
    setShowAppointment(false);
    setPatientData(null); // Clear patient data when closing
  };

  const handlePatientSelect = async (patient) => {
    setPatientData({
      uid: patient.uid,
      name: patient.name,
      email: patient.email,
      mobile: patient.phone_number
    });
    setShowPatientSelection(false);
    setShowAppointment(true);
  };

  const handleClosePatientSelection = () => {
    setShowPatientSelection(false);
    setPatientsList([]);
  };

  const handleLogout = () => {
    localStorage.removeItem('insuranceLoginEmail');
    localStorage.removeItem('insuranceCompany');
    localStorage.removeItem('insuranceKycRequired');
    localStorage.removeItem('insuranceBeneficiaries');
    localStorage.removeItem('insuranceEmployeeMail');
    localStorage.removeItem('insurancePhoneNumber');
    setKycPendingDetails([]);
    setKycBlockedReason('');
    navigate('/insurance/login');
  };

  const handleOpenKYC = () => {
    const email = localStorage.getItem('insuranceLoginEmail');
    const company = localStorage.getItem('insuranceCompany');
    
    if (email && company) {
      // Encode the data in base64 for basic obfuscation
      const data = btoa(JSON.stringify({ email, company }));
      window.open(`https://kyc.toothlens.com/group/${insuranceCompany}/front?auth=${data}`, '_blank');
    } else {
      window.open(`https://kyc.toothlens.com/group/${insuranceCompany}/front`, '_blank');
    }
  };



  return (
    <div className="min-h-screen bg-white flex flex-col">

      {/* Header */}
      <div className="bg-white px-5 py-3 flex items-center justify-end">
        <button
          onClick={handleLogout}
          className="bg-red-500 text-white px-3 py-1.5 rounded-full text-sm font-semibold flex items-center gap-1.5 shadow-sm"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M3 3a1 1 0 00-1 1v12a1 1 0 102 0V4a1 1 0 00-1-1zm10.293 9.293a1 1 0 001.414 1.414l3-3a1 1 0 000-1.414l-3-3a1 1 0 10-1.414 1.414L14.586 9H7a1 1 0 100 2h7.586l-1.293 1.293z" clipRule="evenodd" />
          </svg>
          Logout
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 w-full max-w-lg mx-auto px-5 pb-5">

        <img src={toothlensLogo} alt="Logo" className="h-24 w-auto mx-auto object-contain" />


        {/* Tagline */}
        <h2 className="text-center text-lg font-semibold text-blue-500 mb-6">
          One Plan. Total Protection
        </h2>

                {/* YOUR PLAN INFO */}
                <p className="text-xs font-bold text-gray-600 tracking-widest uppercase mb-2">Your Plan Info</p>
                <div className="grid grid-cols-2 gap-3 mb-5">
                  {/* My Plan Card */}
                  <button
                    onClick={handleExplorePlan}
                    className="bg-gray-50 border-2 border-gray-300 rounded-2xl p-4 text-left hover:shadow-md transition-shadow"
                  >
                    <div className="mb-3">
                      <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                        <ShieldCheck className="h-6 w-6 text-blue-600" />
                      </div>
                    </div>
                    <p className="font-bold text-gray-900 text-base mb-1">My Plan</p>
                    <p className="text-black text-sm mb-3">Coverage, limits & benefits</p>
                    <p className="text-blue-600 font-semibold text-base">Explore &gt;</p>
                  </button>

                  {/* Support Card */}
                  <button
                    onClick={() => setShowSupportPopup(true)}
                    className="bg-orange-50 border-2 border-orange-300 rounded-2xl p-4 text-left hover:shadow-md transition-shadow w-full"
                  >
                    <div className="mb-3">
                      <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center">
                        <Headphones className="h-6 w-6 text-orange-500" />
                      </div>
                    </div>
                    <p className="font-bold text-gray-900 text-base mb-1">Support</p>
                    <p className="text-black text-sm mb-3">Get help with your plan</p>
                    <p className="text-orange-500 font-semibold text-base">Get Help &gt;</p>
                  </button>
                </div>

                {/* HOW TO USE YOUR PLAN */}
                <p className="text-xs font-bold text-gray-600 tracking-widest uppercase mb-2">How To Use Your Plan</p>
                <div className={`relative grid ${kycRequired ? 'grid-cols-2' : 'grid-cols-1'} gap-3 mb-5`}>
                  {/* AI Dental Scan Card — only when KYC required */}
                  {kycRequired && (
                    <button
                      onClick={handleOpenKYC}
                      className="bg-blue-50 border-2 border-blue-300 rounded-2xl p-4 text-left hover:shadow-md transition-shadow"
                    >
                      <div className="flex justify-between items-start mb-3">
                        <span className="bg-blue-500 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold text-sm flex-shrink-0">1</span>
                        <ScanLine className="h-6 w-6 text-blue-400" />
                      </div>
                      <p className="font-bold text-gray-900 text-base mb-1">AI Dental Scan</p>
                      <p className="text-black text-sm mb-3">Complete your scan & activate your plan</p>
                      <p className="text-blue-500 font-semibold text-base">Start &gt;</p>
                    </button>
                  )}

                  {/* Arrow between cards */}
                  {kycRequired && (
                    <div className="absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2 z-10">
                      <div className="w-8 h-8 bg-white rounded-full border border-gray-200 flex items-center justify-center shadow-sm">
                        <ChevronRight className="h-5 w-5 text-gray-500" />
                      </div>
                    </div>
                  )}

                  {/* Book Appointment Card */}
                  <button
                    onClick={handleBookAppointment}
                    className="bg-green-50 border-2 border-green-300 rounded-2xl p-4 text-left hover:shadow-md transition-shadow"
                  >
                    <div className="flex justify-between items-start mb-3">
                      <span className="bg-green-500 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold text-sm flex-shrink-0">
                        {kycRequired ? '2' : '1'}
                      </span>
                      <Calendar className="h-6 w-6 text-green-400" />
                    </div>
                    <p className="font-bold text-gray-900 text-base mb-1">Book Appointment</p>
                    <p className="text-black text-sm mb-3">Find a dentist & book your appointment</p>
                    <p className="text-green-600 font-semibold text-base">Book &gt;</p>
                  </button>
                </div>

                {/* MY ACTIVITY */}
                <p className="text-xs font-bold text-gray-600 tracking-widest uppercase mb-2">My Activity</p>
                <button
                  onClick={() => navigate('/my-bookings')}
                  className="w-full bg-indigo-50 border-2 border-indigo-300 rounded-2xl p-4 text-left hover:shadow-md transition-shadow mb-5"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-indigo-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <ClipboardList className="h-6 w-6 text-indigo-600" />
                    </div>
                    <div className="flex-1">
                      <p className="font-bold text-gray-900 text-base mb-0.5">My Bookings</p>
                      <p className="text-black text-sm">View & manage your appointments</p>
                    </div>
                    <ChevronRight className="h-5 w-5 text-indigo-400" />
                  </div>
                </button>

        {/* Footer */}
        {/* <div className="flex justify-center items-center gap-2 pt-4 pb-6">
          <span className="text-xs text-gray-400">Powered by</span>
          <img src={toothlensLogo} alt="Toothlens" className="h-5 w-auto object-contain" />
        </div> */}
      </div>

      {/* Popup Modal */}
      {showPopup && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="bg-gradient-to-r from-blue-600 to-indigo-600 p-6 rounded-t-2xl sticky top-0">
              <div className="flex justify-between items-start">
                <div>
                  <h2 className="text-2xl font-bold text-white mb-2">Welcome to Toothlens OPD Dental Plan!</h2>
                  <p className="text-blue-100">Your plan gives you more than just coverage—it gives you confidence to smile without worry. Here's what you can do right now:</p>
                </div>
                <button
                  onClick={handleClosePopup}
                  className="text-white hover:bg-white/20 rounded-lg p-2 transition-colors"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>

            <div className="p-6 lg:p-8 space-y-6">
              {/* Plan Benefits */}
              <ul className="space-y-3">
                <li className="flex items-start">
                  <span className="text-blue-500 font-bold mr-3 mt-1">•</span>
                  <span className="text-gray-600 text-sm lg:text-base">
                    <strong>Preventive Care First</strong> – Book free tele consults, teeth cleanings, and routine X-rays to keep your smile healthy year-round.
                  </span>
                </li>
                <li className="flex items-start">
                  <span className="text-blue-500 font-bold mr-3 mt-1">•</span>
                  <span className="text-gray-600 text-sm lg:text-base">
                    <strong>Advanced Treatment Access</strong> – Save on root canals, crowns, braces, and advanced procedures through our trusted dental network.
                  </span>
                </li>
                <li className="flex items-start">
                  <span className="text-blue-500 font-bold mr-3 mt-1">•</span>
                  <span className="text-gray-600 text-sm lg:text-base">
                    <strong>Find a Dentist Near You</strong> – Use the in-app locator to connect with nearby cashless clinics.
                  </span>
                </li>
                <li className="flex items-start">
                  <span className="text-blue-500 font-bold mr-3 mt-1">•</span>
                  <span className="text-gray-600 text-sm lg:text-base">
                    <strong>Cashless & Paperless</strong> – Walk in with your UID, no forms or payments needed upfront.
                  </span>
                </li>
              </ul>

              {/* What is Covered */}
              <div className="border-t pt-6">
                <h3 className="text-gray-800 font-semibold text-lg mb-4">What is Covered:</h3>
                <div className="mb-6">
                  <h4 className="text-gray-700 font-semibold text-base mb-3">Preventive & Minor Procedures</h4>
                  <ul className="space-y-2">
                    <li className="flex items-start"><span className="text-blue-500 font-bold mr-3">•</span><span className="text-gray-600 text-sm lg:text-base">Consultations</span></li>
                    <li className="flex items-start"><span className="text-blue-500 font-bold mr-3">•</span><span className="text-gray-600 text-sm lg:text-base">Diagnostic X-rays</span></li>
                    <li className="flex items-start"><span className="text-blue-500 font-bold mr-3">•</span><span className="text-gray-600 text-sm lg:text-base">Cleanings & Scaling (standard)</span></li>
                    <li className="flex items-start"><span className="text-blue-500 font-bold mr-3">•</span><span className="text-gray-600 text-sm lg:text-base">Fillings (composite/silver)</span></li>
                    <li className="flex items-start"><span className="text-blue-500 font-bold mr-3">•</span><span className="text-gray-600 text-sm lg:text-base">Preventive treatments such as Fluoride treatment, Dental Sealants</span></li>
                    <li className="flex items-start"><span className="text-blue-500 font-bold mr-3">•</span><span className="text-gray-600 text-sm lg:text-base">Minor Oral Surgery procedures</span></li>
                  </ul>
                </div>
                <div className="mb-6">
                  <h4 className="text-gray-700 font-semibold text-base mb-3">Major Procedures</h4>
                  <ul className="space-y-2">
                    <li className="flex items-start"><span className="text-blue-500 font-bold mr-3">•</span><span className="text-gray-600 text-sm lg:text-base">Root Canal Treatment</span></li>
                    <li className="flex items-start"><span className="text-blue-500 font-bold mr-3">•</span><span className="text-gray-600 text-sm lg:text-base">Crowns & Bridges</span></li>
                    <li className="flex items-start"><span className="text-blue-500 font-bold mr-3">•</span><span className="text-gray-600 text-sm lg:text-base">Surgical Extractions</span></li>
                    <li className="flex items-start"><span className="text-blue-500 font-bold mr-3">•</span><span className="text-gray-600 text-sm lg:text-base">Dentures & Partial Dentures</span></li>
                    <li className="flex items-start"><span className="text-blue-500 font-bold mr-3">•</span><span className="text-gray-600 text-sm lg:text-base">Gum treatments</span></li>
                    <li className="flex items-start"><span className="text-blue-500 font-bold mr-3">•</span><span className="text-gray-600 text-sm lg:text-base">Dental Implants</span></li>
                    <li className="flex items-start"><span className="text-blue-500 font-bold mr-3">•</span><span className="text-gray-600 text-sm lg:text-base">Braces</span></li>
                  </ul>
                </div>
              </div>

              {/* What is Not Covered */}
              <div className="border-t pt-6">
                <h3 className="text-gray-800 font-semibold text-lg mb-4">What is Not Covered:</h3>
                <ul className="space-y-2">
                  <li className="flex items-start"><span className="text-red-500 font-bold mr-3">•</span><span className="text-gray-600 text-sm lg:text-base">Elective Cosmetic dental procedures (when not indicated medically)</span></li>
                  <li className="flex items-start"><span className="text-red-500 font-bold mr-3">•</span><span className="text-gray-600 text-sm lg:text-base">Intentional self-inflicted dental injury</span></li>
                  <li className="flex items-start"><span className="text-red-500 font-bold mr-3">•</span><span className="text-gray-600 text-sm lg:text-base">Treatment unrelated to disease/pathology</span></li>
                  <li className="flex items-start"><span className="text-red-500 font-bold mr-3">•</span><span className="text-gray-600 text-sm lg:text-base">Retreatment of previously claimed procedures</span></li>
                  <li className="flex items-start"><span className="text-red-500 font-bold mr-3">•</span><span className="text-gray-600 text-sm lg:text-base">Investigations without clinical necessity</span></li>
                  <li className="flex items-start"><span className="text-red-500 font-bold mr-3">•</span><span className="text-gray-600 text-sm lg:text-base">Dental Products & medicines</span></li>
                  <li className="flex items-start"><span className="text-red-500 font-bold mr-3">•</span><span className="text-gray-600 text-sm lg:text-base">Care outside India</span></li>
                </ul>
              </div>

              {/* Out-of-Network Rules */}
              <div className="border-t pt-6">
                <h3 className="text-gray-800 font-semibold text-lg mb-4">Out-of-Network Rules:</h3>
                <p className="text-gray-700 text-base lg:text-lg leading-relaxed">
                  Out-of-network treatments are allowed, subject to the plan’s coverage, limits, and applicable terms and conditions. To make things easier for members, Toothlens also offers a cashless experience in these cases. We pay the approved amount directly to the treating dentist, and members only need to pay any balance amount, if applicable, due to treatment costs exceeding plan limits or the network tariff. Contact Toothlens Support for more information on out-of-network benefits usage.
                </p>
              </div>

              
            </div>
          </div>
        </div>
      )}

      {/* Support Popup */}
      {showSupportPopup && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-sm w-full shadow-xl">
            {/* Header */}
            <div className="bg-orange-500 p-5 rounded-t-2xl flex justify-between items-center">
              <div className="flex items-center gap-2">
                <Headphones className="h-5 w-5 text-white" />
                <h2 className="text-lg font-bold text-white">Toothlens Support</h2>
              </div>
              <button
                onClick={() => setShowSupportPopup(false)}
                className="text-white hover:text-orange-200 transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Contact Details */}
            <div className="p-6 space-y-5">
              {/* Benefits Assistance */}
              <div>
                <p className="text-xs font-semibold text-orange-500 uppercase tracking-wide mb-2">Benefits Assistance</p>
                <div className="space-y-2">
                  <a href="https://wa.me/919384814056" target="_blank" rel="noreferrer" className="flex items-center gap-3 p-3 rounded-xl bg-gray-50 hover:bg-gray-100 transition-colors">
                    <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <WhatsAppIcon className="w-4 h-4" />
                    </div>
                    <div>
                      <p className="text-xs text-gray-400 mb-0.5">WhatsApp</p>
                      <p className="text-gray-800 font-semibold text-sm">93848 14056</p>
                    </div>
                  </a>
                  <a href="mailto:onboarding@toothlens.com" className="flex items-center gap-3 p-3 rounded-xl bg-gray-50 hover:bg-gray-100 transition-colors">
                    <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <img src={emailIcon} alt="Email" className="w-4 h-4" />
                    </div>
                    <div>
                      <p className="text-xs text-gray-400 mb-0.5">E-mail</p>
                      <p className="text-gray-800 font-semibold text-sm">onboarding@toothlens.com</p>
                    </div>
                  </a>
                </div>
              </div>

              {/* Appointments */}
              <div>
                <p className="text-xs font-semibold text-orange-500 uppercase tracking-wide mb-2">Appointments</p>
                <div className="space-y-2">
                  <a href="https://wa.me/919384820449" target="_blank" rel="noreferrer" className="flex items-center gap-3 p-3 rounded-xl bg-gray-50 hover:bg-gray-100 transition-colors">
                    <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <WhatsAppIcon className="w-4 h-4" />
                    </div>
                    <div>
                      <p className="text-xs text-gray-400 mb-0.5">WhatsApp</p>
                      <p className="text-gray-800 font-semibold text-sm">93848 20449</p>
                    </div>
                  </a>
                  <a href="mailto:appointments@toothlens.com" className="flex items-center gap-3 p-3 rounded-xl bg-gray-50 hover:bg-gray-100 transition-colors">
                    <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <img src={emailIcon} alt="Email" className="w-4 h-4" />
                    </div>
                    <div>
                      <p className="text-xs text-gray-400 mb-0.5">E-mail</p>
                      <p className="text-gray-800 font-semibold text-sm">appointments@toothlens.com</p>
                    </div>
                  </a>
                </div>
              </div>

              {/* Feedback & Concerns */}
              <div>
                <p className="text-xs font-semibold text-orange-500 uppercase tracking-wide mb-2">Feedback &amp; Concerns</p>
                <div className="space-y-2">
                  <a href="mailto:insurance@toothlens.com" className="flex items-center gap-3 p-3 rounded-xl bg-gray-50 hover:bg-gray-100 transition-colors">
                    <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <img src={emailIcon} alt="Email" className="w-4 h-4" />
                    </div>
                    <div>
                      <p className="text-xs text-gray-400 mb-0.5">E-mail</p>
                      <p className="text-gray-800 font-semibold text-sm">insurance@toothlens.com</p>
                    </div>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Loading Modal */}
      {isLoading && !showPatientSelection && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-8 max-w-sm w-full">
            <div className="flex flex-col items-center">
              <div className="relative mb-6">
                <div className="w-20 h-20 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin"></div>
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-10 h-10 bg-blue-600 rounded-full opacity-20 animate-pulse"></div>
                </div>
              </div>
              <h3 className="text-xl font-semibold text-gray-800 mb-2">Loading...</h3>
              <p className="text-gray-600 text-center">Please wait while we fetch your details</p>
            </div>
          </div>
        </div>
      )}

      {/* Error Modal */}
      {errorMessage && !showPatientSelection && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-md w-full">
            <div className="bg-gradient-to-r from-red-600 to-red-700 p-6 rounded-t-2xl">
              <h2 className="text-xl font-bold text-white">Notice</h2>
            </div>
            
            <div className="p-6 space-y-4">
              <div className="text-red-600 text-base">
                {errorMessage}
              </div>
              
              <button
                onClick={() => setErrorMessage('')}
                className="w-full py-3 px-4 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-semibold"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* KYC Status Modal (all-pending or mixed or blocked) */}
      {kycPendingDetails.length > 0 && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-md w-full">
            <div className="bg-gradient-to-r from-blue-600 to-indigo-600 p-6 rounded-t-2xl flex justify-between items-center">
              <div>
                <h2 className="text-xl font-bold text-white">Plan Activation Status</h2>
                <p className="text-blue-100 text-sm mt-1">Member status overview</p>
              </div>
              <button
                onClick={() => { setKycPendingDetails([]); setKycBlockedReason(''); }}
                className="w-8 h-8 bg-white bg-opacity-20 rounded-full flex items-center justify-center hover:bg-opacity-30 transition-all"
              >
                <X className="w-4 h-4 text-white" />
              </button>
            </div>
            {kycBlockedReason ? (
              <div className="mx-6 mt-5 flex items-start gap-3 bg-red-50 border border-red-200 rounded-xl p-4">
                <div className="mt-0.5 flex-shrink-0 w-5 h-5 rounded-full bg-red-500 flex items-center justify-center">
                  <X className="w-3 h-3 text-white" />
                </div>
                <p className="text-sm text-red-700 font-medium">{kycBlockedReason}</p>
              </div>
            ) : null}
            <div className="p-6 space-y-3">
              {kycPendingDetails.map((member) => {
                const statusConfig = {
                  'Active':           { bg: 'bg-green-50',  border: 'border-green-100',  badge: 'bg-green-100 text-green-700' },
                  'Under Review':     { bg: 'bg-blue-50',   border: 'border-blue-100',   badge: 'bg-blue-100 text-blue-700' },
                  'In Process':       { bg: 'bg-blue-50',   border: 'border-blue-100',   badge: 'bg-blue-100 text-blue-700' },
                  'Retake Requested': { bg: 'bg-amber-50',  border: 'border-amber-100',  badge: 'bg-amber-100 text-amber-700' },
                  'X-Ray Requested':  { bg: 'bg-amber-50',  border: 'border-amber-100',  badge: 'bg-amber-100 text-amber-700' },
                  'Rejected':         { bg: 'bg-red-50',    border: 'border-red-100',    badge: 'bg-red-100 text-red-700' },
                  'Pending':          { bg: 'bg-orange-50', border: 'border-orange-100', badge: 'bg-orange-100 text-orange-700' },
                }[member.status] || { bg: 'bg-gray-50', border: 'border-gray-100', badge: 'bg-gray-100 text-gray-700' };

                return (
                  <div
                    key={member.uid}
                    className={`flex items-center justify-between p-3 rounded-xl border ${statusConfig.bg} ${statusConfig.border}`}
                  >
                    <div className="min-w-0">
                      <p className="font-semibold text-gray-800 capitalize truncate">{member.name}</p>
                      <p className="text-sm text-gray-500 capitalize">{member.relation}</p>
                      <span
                        title={member.uid}
                        className="inline-block max-w-full truncate mt-1 text-[11px] font-mono font-medium text-indigo-700 bg-indigo-50 border border-indigo-200 px-1.5 py-0.5 rounded"
                      >
                        {member.uid}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`text-xs font-semibold px-3 py-1 rounded-full ${statusConfig.badge}`}>
                        {member.status}
                      </span>
                      {member.canBook && member.patientData && (
                        <button
                          onClick={() => {
                            setPatientData(member.patientData);
                            setKycPendingDetails([]);
                            setShowAppointment(true);
                          }}
                          className="text-xs font-semibold bg-blue-600 text-white px-3 py-1 rounded-full hover:bg-blue-700 transition-colors"
                        >
                          Book
                        </button>
                      )}
                      {member.status === 'Pending' && (
                        <button
                          onClick={() => { setKycPendingDetails([]); handleOpenKYC(); }}
                          className="text-xs font-semibold bg-orange-500 text-white px-3 py-1 rounded-full hover:bg-orange-600 transition-colors"
                        >
                          Complete Scan
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Appointment Popup Modal */}
      {showAppointment && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-4xl w-full h-[90vh] flex flex-col">
            <div className="flex-1 overflow-y-auto rounded-2xl">
              <PlumAppointment
                prefillData={patientData}
                onClose={handleCloseAppointment}
              />
            </div>
          </div>
        </div>
      )}

      {/* Patient Selection Modal */}
      {showPatientSelection && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-md w-full max-h-[85vh] flex flex-col shadow-2xl">
            {/* Header */}
            <div className="bg-gradient-to-r from-blue-600 to-indigo-600 p-6 rounded-t-2xl flex justify-between items-center flex-shrink-0">
              <div>
                <h2 className="text-xl font-bold text-white">Choose Member</h2>
                <p className="text-blue-100 text-sm mt-1">Select a member to book an appointment</p>
              </div>
              <button
                onClick={handleClosePatientSelection}
                className="w-8 h-8 bg-white bg-opacity-20 rounded-full flex items-center justify-center hover:bg-opacity-30 transition-all"
                disabled={isLoading}
              >
                <X className="w-4 h-4 text-white" />
              </button>
            </div>

            {/* Member list */}
            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              {patientsList.map((patient, idx) => (
                <div
                  key={patient._id || patient.uid || idx}
                  className="flex items-center justify-between p-4 rounded-xl border border-gray-100 bg-gray-50 hover:bg-blue-50 hover:border-blue-200 transition-all"
                >
                  {/* Avatar + info */}
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-indigo-500 flex items-center justify-center flex-shrink-0">
                      <span className="text-white font-bold text-sm">
                        {(patient.name || '?').charAt(0).toUpperCase()}
                      </span>
                    </div>
                    <div>
                      <p className="font-semibold text-gray-800 capitalize">{patient.name}</p>
                      <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                        {patient.relation_to_patient && (
                          <span className="text-xs font-medium bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded-full capitalize">
                            {patient.relation_to_patient}
                          </span>
                        )}
                        {patient.phone_number && (
                          <span className="text-xs text-gray-500">{patient.phone_number}</span>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Book button */}
                  <button
                    onClick={() => handlePatientSelect(patient)}
                    className="flex-shrink-0 flex items-center gap-1.5 bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-semibold px-4 py-2 rounded-full transition-all shadow-sm hover:shadow-md"
                  >
                    <Calendar className="w-3.5 h-3.5" />
                    Book
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Session Timeout Modal */}
      {showSessionTimeout && (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-md w-full">
            <div className="bg-gradient-to-r from-orange-600 to-red-600 p-6 rounded-t-2xl">
              <div className="flex items-center gap-3">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-white" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
                <h2 className="text-xl font-bold text-white">Session Timeout</h2>
              </div>
            </div>
            
            <div className="p-6 space-y-4">
              <div className="text-gray-700 text-base">
                Your session has expired after 2 hours for security reasons. Please login again to continue.
              </div>
              
              <button
                onClick={() => {
                  localStorage.removeItem('insuranceLoginEmail');
                  localStorage.removeItem('insuranceCompany');
                  navigate('/insurance/login');
                }}
                className="w-full py-3 px-4 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-lg hover:from-blue-700 hover:to-indigo-700 transition-all font-semibold"
              >
                Go to Login
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TestingIframeLink;